/**
 * CountDown - A vanilla JavaScript countdown timer
 * This class creates and manages a countdown timer that displays days, hours, minutes, and seconds
 * remaining until a specified deadline.
 */
class CountDown {
  /**
   * Creates a new CountDown instance
   */
  constructor() {
    this.element = document.querySelector('#global-countdown');
    this.navbar = document.querySelector('.navbar');
    this.deadline = new Date(2025, 5, 4); // June 4th, 2025 (month is 0-indexed, so 5 = June)
    this.timeinterval = null;
    
    // Bind methods to preserve 'this' context
    this.updateNavbarPosition = this.updateNavbarPosition.bind(this);
  }

  /**
   * Checks if the countdown element exists in the DOM
   * @returns {boolean} True if the countdown element exists
   */
  countdownExists() {
    return !!this.element;
  }

  /**
   * Updates the navbar position based on the countdown height
   */
  updateNavbarPosition() {
    if (this.navbar && this.element) {
      const countdownHeight = this.element.offsetHeight;
      this.navbar.style.top = `${countdownHeight}px`;
      this.updatePagePadding();
    }
  }

  /**
   * Updates the page padding to prevent content overlap with fixed elements
   */
  updatePagePadding() {
    const page = document.getElementById('page');
    if (page && this.navbar && this.element) {
      const countdownHeight = this.element.offsetHeight;
      const navbarHeight = this.navbar.offsetHeight;
      const totalHeight = countdownHeight + navbarHeight;
      page.style.paddingTop = `${totalHeight}px`;
    }
  }
  
  /**
   * Calculates time remaining until the deadline
   * @param {Date} endtime - The deadline date
   * @returns {Object} Object containing total milliseconds and time units
   */
  getTimeRemaining(endtime) {
    const total = Date.parse(endtime) - Date.now();
    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const days = Math.floor(total / (1000 * 60 * 60 * 24));

    return {
      total,
      days,
      hours,
      minutes,
      seconds,
    };
  }

  /**
   * Initializes the countdown clock
   */
  initializeClock() {
    if (!this.element) return;
    
    const daysSpan = this.element.querySelector('.days');
    const hoursSpan = this.element.querySelector('.hours');
    const minutesSpan = this.element.querySelector('.minutes');
    const secondsSpan = this.element.querySelector('.seconds');
    
    if (!daysSpan || !hoursSpan || !minutesSpan || !secondsSpan) {
      console.error('Required countdown elements not found');
      return;
    }

    const updateClock = () => {
      const t = this.getTimeRemaining(this.deadline);
      
      // Update the DOM elements with the new values
      daysSpan.textContent = t.days;
      hoursSpan.textContent = (`0${t.hours}`).slice(-2);
      minutesSpan.textContent = (`0${t.minutes}`).slice(-2);
      secondsSpan.textContent = (`0${t.seconds}`).slice(-2);

      // Stop the countdown when we reach the deadline
      if (t.total <= 0) {
        clearInterval(this.timeinterval);
        
        // Hide the countdown element when deadline is reached
        if (this.element) {
          this.element.style.display = 'none';
          
          // Reset navbar position and page padding
          if (this.navbar) {
            this.navbar.style.top = '0';
            this.updatePagePadding();
          }
        }
      }
    };
    
    // Initialize and start the countdown
    updateClock();
    this.timeinterval = setInterval(updateClock, 1000);
  }

  /**
   * Initializes the countdown if the element exists
   */
  init() {
    if (this.countdownExists()) {
      this.initializeClock();
      this.updateNavbarPosition();
      this.updatePagePadding();
      
      // Add event listener for window resize
      window.addEventListener('resize', this.updateNavbarPosition);
    }
  }

  /**
   * Cleans up event listeners and intervals
   */
  destroy() {
    if (this.timeinterval) {
      clearInterval(this.timeinterval);
    }
    window.removeEventListener('resize', this.updateNavbarPosition);
  }
}

export default CountDown;